import { memo, useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  changeDataDrawer,
  checkPeriodsSaveDisabled,
  deletePeriod,
  discardChanges,
  firstDrawerSelector,
  periodsMapSelector,
  savePeriod,
  secondDrawerSelector,
  setFirstDrawer,
  setSecondDrawer,
} from 'slices/period.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { DrawerCallback } from 'types/utils.type';
import type { TPeriod } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import PeriodDetail from './sections';

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const PeriodDrawer = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstDrawer = useAppSelector(firstDrawerSelector);
  const secondDrawer = useAppSelector(secondDrawerSelector);
  const periodsMap: Record<number | string, TPeriod> =
    useAppSelector(periodsMapSelector);

  const setFirstResource = useCallback(
    (period: TPeriod) => dispatch(setFirstDrawer(period)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (period: TPeriod) => dispatch(setSecondDrawer(period)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    periodsMap,
    firstDrawer,
    secondDrawer,
    setFirstResource,
    setSecondResource
  );

  const firstPaddingRuleSaveDisabled = useMemo(
    () => dispatch(checkPeriodsSaveDisabled(true)),
    [firstDrawer]
  );

  const secondDrawerSaveDisabled = useMemo(
    () => dispatch(checkPeriodsSaveDisabled(false)),
    [secondDrawer]
  );

  const onDelete = useCallback((period: TPeriod, isFirstDrawer: boolean) => {
    dispatch(deletePeriod({ periodId: period.id, isFirstDrawer }));
  }, []);

  const onSave = useCallback((isFirstDrawer: boolean, period: TPeriod) => {
    dispatch(savePeriod({ isFirstDrawer, period }));
  }, []);

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstDrawer(undefined));
        } else {
          dispatch(setSecondDrawer(undefined));
        }
      }),
    []
  );

  const onChange = useCallback<DrawerCallback<TPeriod>>(
    (isFirstDrawer, key, value) =>
      dispatch(changeDataDrawer({ isFirstDrawer, key, value })),
    []
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstDrawer?.name || '',
        content: (
          <PeriodDetail
            data={firstDrawer}
            onChange={(key, value) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstDrawer,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('period_confirmation_delete')}
            handleDelete={() => onDelete(firstDrawer!, true)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstDrawer!.id,
                })
              )
            }
            handleSave={() => onSave(true, firstDrawer!)}
            isSaveDisable={firstPaddingRuleSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondDrawer?.name || '',
        content: (
          <PeriodDetail
            data={secondDrawer}
            onChange={(key, value) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondDrawer,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('period_confirmation_delete')}
            handleDelete={() => onDelete(secondDrawer!, false)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondDrawer!.id,
                })
              )
            }
            handleSave={() => onSave(false, secondDrawer!)}
            isSaveDisable={secondDrawerSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default memo(PeriodDrawer);
